import type { User } from "~/types/user";

import { normalizeDateToMidnight } from "./utils";

type EpisodeAccessInput = {
  permissions?: unknown;
  releaseDate?: string | null;
};

type EpisodeAccessResult = {
  canAccess: boolean;
  shouldRedirect: boolean;
};

function userHasPlus(user: User | null): boolean {
  return Boolean(user?.hasDevoPlus);
}

export function permissionsIncludeDevoPlus(permissions: unknown): boolean {
  if (!permissions || !Array.isArray(permissions)) return false;

  return permissions.some((perm) => {
    // Simple string case
    if (typeof perm === "string") {
      return perm === "+devo" || perm === "devo+";
    }

    // Sanity object case
    if (perm && typeof perm === "object") {
      const values = Object.values(perm as Record<string, unknown>);
      return values.some((val) => val === "+devo" || val === "devo+");
    }

    return false;
  });
}

// Function to valid the free period of the series with the realese date from the episode
export function permissionsIncludeFreePeriod(freePeriod: number, releaseDate: string): boolean {
  if (!freePeriod) return false;

  // Check if the episode is within the free period
  const today = normalizeDateToMidnight(new Date());
  const cutoffDate = normalizeDateToMidnight(new Date(today));
  cutoffDate.setDate(cutoffDate.getDate() - freePeriod);

  // Parse YYYY-MM-DD manually to create local midnight date
  // This prevents "2025-11-27" (UTC) becoming "2025-11-26 18:00" in CST
  const [y, m, d] = releaseDate.split("-").map(Number);
  const episodeReleaseDate = new Date(y, m - 1, d);
  episodeReleaseDate.setHours(0, 0, 0, 0);

  // If episode is outside the freePeriod window, it requires Sing+
  const isOutsideWindow = episodeReleaseDate.getTime() < cutoffDate.getTime();
  return isOutsideWindow;
}

/**
 * Check if an episode requires Sing+ access for free users
 * Based on permissions field and freePeriod rule for series
 * @param options - Object containing episode data and user status
 * @param options.permissions - Episode permissions array
 * @param options.releaseDate - Episode release date string
 * @param options.hasDevoPlus - Whether the user has Devo+ subscription
 * @param options.freePeriod - The number of days in the free period for the series (null/undefined if not set)
 * @returns true if the episode requires Sing+ access, false otherwise
 */
export function requiresSingPlus(options: {
  permissions?: Array<string> | null;
  releaseDate?: string | null;
  hasDevoPlus: boolean;
  freePeriod?: number | null | undefined;
}): boolean {
  const { permissions, releaseDate, hasDevoPlus, freePeriod } = options;

  // Paid users always have access
  if (hasDevoPlus) {
    return false;
  }

  // Check if episode has explicit 'devo+' permission
  if (permissions && permissionsIncludeDevoPlus(permissions)) {
    return true;
  }

  // If series has freePeriod, check if episode is outside the free period window
  if (freePeriod != null && releaseDate) {
    const today = normalizeDateToMidnight(new Date());
    const cutoffDate = normalizeDateToMidnight(new Date(today));
    cutoffDate.setDate(cutoffDate.getDate() - freePeriod);

    // Parse YYYY-MM-DD manually to create local midnight date
    const [y, m, d] = releaseDate.split("-").map(Number);
    const episodeReleaseDate = new Date(y, m - 1, d);
    episodeReleaseDate.setHours(0, 0, 0, 0);

    // If episode is outside the freePeriod window, it requires Sing+
    const isOutsideWindow = episodeReleaseDate.getTime() < cutoffDate.getTime();
    return isOutsideWindow;
  }

  return false;
}

/**
 * Check if a free user can access an episode based on permissions and series freePeriod
 */
export function checkEpisodeAccess(
  episode: EpisodeAccessInput | null,
  freePeriod: number | null | undefined,
  user: User | null,
  episodeReleaseDate?: string | null,
): EpisodeAccessResult {
  // 1) Check if user is authenticated, if not, do not allow access and do not redirect
  if (!user) {
    return { canAccess: false, shouldRedirect: false };
  }

  // 2) Users with Sing+ always have access
  if (userHasPlus(user)) {
    return { canAccess: true, shouldRedirect: false };
  }

  // 3) Check if episode has explicit 'devo+' permission
  const permissions = episode?.permissions;
  if (permissionsIncludeDevoPlus(permissions)) {
    return { canAccess: false, shouldRedirect: true };
  }

  // 4) If series has freePeriod, check if episode is within the free period window
  if (freePeriod != null) {
    const releaseDate = episodeReleaseDate ?? episode?.releaseDate ?? null;
    if (!releaseDate || typeof releaseDate !== "string") {
      // If no release date, default to accessible
      return { canAccess: true, shouldRedirect: false };
    }

    const today = normalizeDateToMidnight(new Date());
    const cutoffDate = normalizeDateToMidnight(new Date(today));
    cutoffDate.setDate(cutoffDate.getDate() - freePeriod);

    // Parse YYYY-MM-DD manually to create local midnight date
    const [y, m, d] = releaseDate.split("-").map(Number);
    const episodeDate = new Date(y, m - 1, d);
    episodeDate.setHours(0, 0, 0, 0);

    const isWithinWindow = episodeDate.getTime() >= cutoffDate.getTime();

    if (!isWithinWindow) {
      return { canAccess: false, shouldRedirect: true };
    }

    return { canAccess: true, shouldRedirect: false };
  }

  // 5) If series doesn't have freePeriod, episode is accessible (unless it has devo+ permission, which was already checked)
  return { canAccess: true, shouldRedirect: false };
}
